import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CurriculumApi, PersonApi } from '../+api';
import { CurriculumInterface, Permissions } from '../+models';
import { DalState } from '../+state';
import { UserQueries } from '../+state/user';
import { BearerHttpService } from '../bearer-http';
import { DAL_OPTIONS, DalOptions } from '../dal.module';
import { CurriculumServiceInterface } from './curriculum.service.interface';

@Injectable({
  providedIn: 'root',
})
export class CurriculumService implements CurriculumServiceInterface {
  private dalOptions = inject<DalOptions>(DAL_OPTIONS);
  private http = inject(HttpClient);
  private bearerHttp = inject(BearerHttpService);
  private store = inject<Store<DalState>>(Store);

  constructor(
    private personApi: PersonApi,
    private curriculumApi: CurriculumApi
  ) {}

  public getAllForUser(userId: number): Observable<CurriculumInterface[]> {
    return this.personApi
      .getData(userId, 'curriculums')
      .pipe(map((res: { curriculums: CurriculumInterface[] }) => res.curriculums));
  }

  public publishCurriculum(learningAreaId: number): Observable<CurriculumInterface> {
    return this.curriculumApi.publishForLearningArea(learningAreaId);
  }

  public publishCurriculumForGoalGroup(goalGroupId: number): Observable<CurriculumInterface> {
    return this.store.pipe(
      select(UserQueries.getPermissions),
      take(1),
      switchMap((permissions) => {
        const hasPermission = permissions.includes(Permissions.cms.CMS_PUBLISH_CURRICULUM_V2);
        const { apiBaseUrl, apiV2BaseUrl } = this.dalOptions;

        if (hasPermission && apiV2BaseUrl) {
          const url = `${apiV2BaseUrl}/curriculum/${goalGroupId}/publish`;

          return this.bearerHttp.post<CurriculumInterface>(url, {});
        }

        const url = `${apiBaseUrl}/api/Curricula/publish-for-goal-group/${goalGroupId}`;

        return this.http.get<CurriculumInterface>(url, {
          withCredentials: true,
        });
      })
    );
  }

  public publishCurriculumContent(curriculumGuid: string): Observable<void> {
    const { apiBaseUrl } = this.dalOptions;
    const url = `${apiBaseUrl}/api/Curricula/${curriculumGuid}/publish-content`;

    return this.http.get<void>(url, { withCredentials: true });
  }
}
